import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, DollarSign, Activity, Users, Star, Calendar, Clock, MapPin, Car, Target, Zap, Award, Medal, Trophy } from 'lucide-react';
import { API_URL } from './config';

const EnhancedDriverAnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    fetchDriverStats();
  }, [selectedPeriod]);

  const fetchDriverStats = async () => {
    try {
      setLoading(true);
      // Fetch all drivers first
      const driversResponse = await fetch(`${API_URL}/driver/`);
      const driversData = await driversResponse.json();

      // Fetch comprehensive analytics for each driver
      const analyticsPromises = driversData.map(async (driver) => {
        try {
          const analyticsResponse = await fetch(`${API_URL}/driver/${driver.driver_id}/comprehensive_stats`);
          const analyticsData = await analyticsResponse.json();
          
          if (analyticsResponse.ok) {
            return {
              driverId: driver.driver_id,
              driverInfo: driver,
              analytics: analyticsData
            };
          }
        } catch (error) {
          console.error(`Error fetching analytics for driver ${driver.driver_id}:`, error);
        }
        return null;
      });

      const analyticsResults = await Promise.all(analyticsPromises);
      const validAnalytics = analyticsResults.filter(result => result !== null);

      setStats({ drivers: validAnalytics });
      setError(null);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = () => {
    fetchDriverStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600">
            <Activity className="h-5 w-5" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !stats.drivers) {
    return <div>No driver data available</div>;
  }

  // Calculate summary metrics
  const totalDrivers = stats.drivers.length;
  const totalEarnings = stats.drivers.reduce((sum, driver) => sum + (driver.analytics.earnings.combined.total_earnings || 0), 0);
  const totalTrips = stats.drivers.reduce((sum, driver) => sum + (driver.analytics.trip_statistics.combined.total_trips || 0), 0);
  const avgEarningsPerDriver = totalDrivers > 0 ? totalEarnings / totalDrivers : 0;
  const avgTripsPerDriver = totalDrivers > 0 ? totalTrips / totalDrivers : 0;

  // Top performers
  const topEarners = stats.drivers
    .sort((a, b) => (b.analytics.earnings.combined.total_earnings || 0) - (a.analytics.earnings.combined.total_earnings || 0))
    .slice(0, 5);

  const mostActiveDrivers = stats.drivers
    .sort((a, b) => (b.analytics.trip_statistics.combined.total_trips || 0) - (a.analytics.trip_statistics.combined.total_trips || 0))
    .slice(0, 5);

  const topRatedDrivers = stats.drivers
    .filter(driver => driver.analytics?.ratings?.received?.combined?.average_received > 0)
    .sort((a, b) => (b.analytics?.ratings?.received?.combined?.average_received || 0) - (a.analytics?.ratings?.received?.combined?.average_received || 0))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enhanced Driver Analytics</h1>
          <p className="text-gray-600">Comprehensive driver performance and earnings analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Drivers
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">{totalDrivers}</div>
            <p className="text-xs text-muted-foreground">
              Active drivers in period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Earnings
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">${totalEarnings.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Avg per driver: ${avgEarningsPerDriver.toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Total Trips
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">{totalTrips}</div>
            <p className="text-xs text-muted-foreground">
              Avg per driver: {avgTripsPerDriver.toFixed(1)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Avg Completion Rate
                </Typography>
              }
            />
          <CardContent>
            <div className="text-2xl font-bold">
              {(stats.drivers.reduce((sum, driver) => sum + (driver.analytics.performance.completion_rate || 0), 0) / totalDrivers).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Across all drivers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Earners */}
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Top Earners
                </Typography>
              }
            />
          <CardContent>
            <div className="space-y-4">
              {topEarners.map((driver, index) => (
                <div key={driver.driverId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{driver.driverInfo.name || driver.driverId}</div>
                      <div className="text-sm text-gray-600">{driver.driverId}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">
                      ${(driver.analytics.earnings.combined.total_earnings || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {driver.analytics.trip_statistics.combined.total_trips || 0} trips
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Most Active Drivers */}
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Most Active Drivers
                </Typography>
              }
            />
          <CardContent>
            <div className="space-y-4">
              {mostActiveDrivers.map((driver, index) => (
                <div key={driver.driverId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{driver.driverInfo.name || driver.driverId}</div>
                      <div className="text-sm text-gray-600">{driver.driverId}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-blue-600">
                      {driver.analytics.trip_statistics.combined.total_trips || 0} trips
                    </div>
                    <div className="text-sm text-gray-600">
                      ${(driver.analytics.earnings.combined.total_earnings || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Rated Drivers */}
        <Card>
          <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Top Rated Drivers
                </Typography>
              }
            />
          <CardContent>
            <div className="space-y-4">
              {topRatedDrivers.map((driver, index) => (
                <div key={driver.driverId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{driver.driverInfo.name || driver.driverId}</div>
                      <div className="text-sm text-gray-600">{driver.driverId}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                      <span className="font-bold text-yellow-600">
                        {(driver.analytics?.ratings?.received?.combined?.average_received || 0).toFixed(1)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {driver.analytics?.ratings?.received?.combined?.total_received || 0} ratings
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Driver Performance Breakdown */}
      <Card>
        <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Driver Performance Breakdown
                </Typography>
              }
            />
        <CardContent>
          <div className="space-y-6">
            {/* Performance Distribution */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Performance Distribution</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.drivers.filter(d => (d.analytics.performance.completion_rate || 0) >= 90).length}
                  </div>
                  <div className="text-sm text-green-800">Excellent (90%+)</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.drivers.filter(d => (d.analytics.performance.completion_rate || 0) >= 70 && (d.analytics.performance.completion_rate || 0) < 90).length}
                  </div>
                  <div className="text-sm text-blue-800">Good (70-89%)</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {stats.drivers.filter(d => (d.analytics.performance.completion_rate || 0) < 70).length}
                  </div>
                  <div className="text-sm text-yellow-800">Needs Improvement (&lt;70%)</div>
                </div>
              </div>
            </div>

            {/* Activity Distribution */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Activity Distribution</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.drivers.filter(d => (d.analytics.activity.days_active || 0) > 20).length}
                  </div>
                  <div className="text-sm text-purple-800">Very Active (20+ days)</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {stats.drivers.filter(d => (d.analytics.activity.days_active || 0) >= 10 && (d.analytics.activity.days_active || 0) <= 20).length}
                  </div>
                  <div className="text-sm text-orange-800">Active (10-20 days)</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">
                    {stats.drivers.filter(d => (d.analytics.activity.days_active || 0) < 10).length}
                  </div>
                  <div className="text-sm text-gray-800">Occasional (&lt;10 days)</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Earnings Breakdown */}
      <Card>
        <CardHeader
              title={
                <Typography variant="h6" component="div">
                  Earnings Breakdown
                </Typography>
              }
            />
        <CardContent>
          <div className="space-y-6">
            {/* Total Earnings by Type */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Earnings by Trip Type</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    ${stats.drivers.reduce((sum, d) => sum + (d.analytics.earnings.private.total_earnings || 0), 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-blue-800">Private Rides</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    ${stats.drivers.reduce((sum, d) => sum + (d.analytics.earnings.rideshare.total_earnings || 0), 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-green-800">Rideshare</div>
                </div>
              </div>
            </div>

            {/* Average Earnings */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Average Earnings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    ${(stats.drivers.reduce((sum, d) => sum + (d.analytics.earnings.private.average_per_trip || 0), 0) / totalDrivers).toFixed(2)}
                  </div>
                  <div className="text-sm text-purple-800">Private per trip</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    ${(stats.drivers.reduce((sum, d) => sum + (d.analytics.earnings.rideshare.average_per_trip || 0), 0) / totalDrivers).toFixed(2)}
                  </div>
                  <div className="text-sm text-orange-800">Rideshare per trip</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedDriverAnalyticsDashboard;
